/**
 * @lines98/client/lib/session-revoked — «сессия отозвана» pub/sub seam.
 *
 * ЧТО: один источник правды для события «текущая авторизованная сессия отозвана
 *   в другом месте» (вход в аккаунт с другого браузера/устройства ротирует
 *   `players.secret_token_hash` → старый Bearer этого браузера получает 401
 *   `token_not_recognized` на ранее работавшем authed-запросе).
 *
 * ЗАЧЕМ: `api-client` (ствол всех authed-запросов, DOM-free) ловит revoked-401,
 *   но НЕ должен сам рисовать плашку/трогать экраны (нарушит модульность и
 *   затащит DOM в сетевой слой). Он лишь ПУБЛИКУЕТ сигнал сюда; app-shell
 *   ПОДПИСЫВАЕТСЯ один раз и показывает плашку (`lib/session-revoked-banner`).
 *   Это разрывает цикл api-client → UI и держит «ствол → ветки» (CLAUDE.md
 *   owner-directive 2026-05-30): сигнал — переиспользуемый модуль, не копия.
 *
 * SCOPE: только клиентский UX-сигнал над уже-существующим серверным 401.
 *   Серверную auth / анти-чит НЕ трогает (incident 2026-06-10 — тихий kick
 *   создавал призрачного анонима, который молча играл доску дня).
 *
 * Дизайн: де-дуп через `_pending` — пока плашка не закрыта/не обработана,
 *   повторные revoked-401 (несколько параллельных запросов 401-ят разом) НЕ
 *   плодят повторные эмиты. `markSessionRevokedHandled()` снимает флаг.
 *
 * Cross-references:
 *   - services/api-client.ts (publisher — revoked-detect в request()).
 *   - lib/session-revoked-banner.ts (subscriber action — плашка + cleanup).
 *   - app.ts bootShell (installs the listener once).
 */

/** Слушатель revoked-события. Без аргументов — сам факт revoked несёт всё. */
export type SessionRevokedListener = () => void;

/**
 * Внутреннее состояние seam'а (слушатели + флаг эпизода) ЯКОРИТСЯ на `globalThis`,
 * а не на module-local-переменных.
 *
 * ЗАЧЕМ: seam — кросс-вкладочный/кросс-модульный bus (api-client публикует, app-shell
 *   и `session-revoked-broadcast` подписываются). Он ОБЯЗАН быть единственным
 *   логическим экземпляром на страницу. При статической сборке модуль и так
 *   синглтон, НО под тестовым `vi.resetModules()` (мост-тест динамически
 *   ре-импортит `session-revoked-broadcast`, тянущий свежую копию этого модуля)
 *   module-local-переменные раздваивались: подписка теста жила на одном экземпляре,
 *   а `emitSessionRevoked` из ре-импортнутого моста — на другом → сигнал не доходил.
 *   Якорь на `globalThis` делает state общим для ЛЮБОЙ копии модуля — один bus,
 *   как и в проде. Чистый рефактор хранилища: публичный контракт не изменился.
 */
interface SessionRevokedState {
  listeners: Set<SessionRevokedListener>;
  /**
   * `true` пока активный revoked-эпизод не обработан (плашка открыта / выбор не
   * сделан). Гасит повторные эмиты от параллельных 401-ящих запросов — чтобы не
   * наслаивать плашки. Снимается `markSessionRevokedHandled()`.
   */
  pending: boolean;
}

const STATE_KEY = '__lines98_sessionRevokedState__';

function getState(): SessionRevokedState {
  const g = globalThis as Record<string, unknown>;
  let state = g[STATE_KEY] as SessionRevokedState | undefined;
  if (!state) {
    state = { listeners: new Set<SessionRevokedListener>(), pending: false };
    g[STATE_KEY] = state;
  }
  return state;
}

/**
 * Подписаться на revoked-событие. Возвращает unsubscribe-функцию.
 * App-shell вызывает один раз на boot.
 */
export function onSessionRevoked(listener: SessionRevokedListener): () => void {
  const state = getState();
  state.listeners.add(listener);
  return () => {
    state.listeners.delete(listener);
  };
}

/**
 * Опубликовать «сессия отозвана». Вызывается ТОЛЬКО api-client'ом при детекте
 * revoked-401 на ранее авторизованной identity.
 *
 * Идемпотентно в рамках эпизода: если уже `_pending`, повторный вызов — no-op
 * (несколько параллельных запросов отвалятся 401 разом, но плашка — одна).
 * Сбрасывается `markSessionRevokedHandled()` после обработки выбора игроком.
 */
export function emitSessionRevoked(): void {
  const state = getState();
  if (state.pending) return;
  state.pending = true;
  for (const listener of state.listeners) {
    try {
      listener();
    } catch {
      // Слушатель-ветка не должен ронять сетевой ствол.
    }
  }
}

/** Активен ли необработанный revoked-эпизод (для подписчика — анти-дубль). */
export function isSessionRevokedPending(): boolean {
  return getState().pending;
}

/**
 * Снять флаг эпизода после обработки (плашка закрыта, identity очищена/выбор
 * сделан). Следующий revoked-401 снова сможет поднять плашку.
 */
export function markSessionRevokedHandled(): void {
  getState().pending = false;
}

/** Test seam: сброс внутреннего состояния между кейсами. */
export function _resetForTests(): void {
  const state = getState();
  state.listeners.clear();
  state.pending = false;
}
